import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Input, Select, Button, message, Spin, Card, Typography, Space, Tag, Divider, Alert, Tooltip, Badge, Collapse, Steps, Tabs, Radio } from 'antd';
import { 
  RobotOutlined, PlayCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, 
  ReloadOutlined, ThunderboltOutlined, CodeOutlined, BugOutlined, SyncOutlined,
  CaretRightOutlined, ExclamationCircleOutlined, InfoCircleOutlined, CopyOutlined, CheckOutlined,
  FileTextOutlined, BarChartOutlined
} from '@ant-design/icons';
import { judgeScenarioApi } from '../services/api';
import CodeEditor from './CodeEditor';
import ResultStatsDisplay from './ResultStatsDisplay';
import AutoReviewStatsDisplay from './AutoReviewStatsDisplay';

const { TextArea } = Input;
const { Text, Title, Paragraph } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

// 脚本类型配置（调试模式使用）
const SCRIPT_TYPES = [
  { 
    value: 'PRE_SCRIPT', 
    label: '前置脚本', 
    desc: '在服务调用前执行，准备请求参数',
    tips: '可访问 useCase 对象，返回值作为服务调用参数',
    category: 'common'
  },
  { 
    value: 'ASSERTION_SERVICE_SCRIPT', 
    label: '断言服务脚本', 
    desc: '调用外部断言服务（可选）',
    tips: '用于获取额外的判断依据',
    category: 'common'
  },
  { 
    value: 'ASSERTION_SCRIPT', 
    label: '断言脚本', 
    desc: '验证服务响应与期望答案',
    tips: '机审场景需输出 machineResult/expectedResult',
    category: 'common'
  },
  { 
    value: 'METRIC_STATS_SCRIPT', 
    label: '指标统计脚本', 
    desc: '指标统计脚本（要素抽取和机审场景通用）',
    tips: '可访问 useCases/executionResults/presetMetrics/metricConfig',
    category: 'common'
  },
];

// 场景类型
const SCENARIO_CATEGORIES = {
  'FEATURE_EXTRACTION': '要素抽取',
  'AUTO_REVIEW': '机审场景',
  'ROBOT_QA': '机器人问答',
};

/**
 * AI脚本生成/调试弹窗组件
 * mode='generate': 生成模式 - 进度条：0-选择配置 → 1-生成脚本 → 2-调试验证
 * mode='debug': 调试模式 - 直接进入调试验证步骤，使用表单中的脚本
 * defaultScriptType: 默认选中的脚本类型
 */
function AiScriptGenerateModal({ visible, onCancel, scenario, onApply, serviceConfig, scenarioCategory, getFormScripts, mode = 'generate', defaultScriptType = 'ASSERTION_SCRIPT' }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  
  // 是否为调试模式
  const isDebugMode = mode === 'debug';
  
  // 当前选中的脚本类型 - 机审场景默认选择前置脚本，其他场景默认选择断言脚本
  const [currentScriptType, setCurrentScriptType] = useState(
    scenarioCategory === 'AUTO_REVIEW' ? 'PRE_SCRIPT' : 'ASSERTION_SCRIPT'
  );
  
  // 各类型脚本
  const [scripts, setScripts] = useState({
    PRE_SCRIPT: '',
    ASSERTION_SERVICE_SCRIPT: '',
    ASSERTION_SCRIPT: '',
    METRIC_STATS_SCRIPT: '',
  });
  
  // 一键生成进度
  const [generateProgress, setGenerateProgress] = useState({
    currentStep: '',
    statusMessage: '',
    isGenerating: false,
  });
  
  // 样例用例列表（调试模式使用）
  const [sampleUseCases, setSampleUseCases] = useState([]);
  const [selectedUseCaseId, setSelectedUseCaseId] = useState(null);
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  
  // 调试相关 - 使用对象存储每个脚本类型的调试结果
  const [debugResults, setDebugResults] = useState({}); // { PRE_SCRIPT: {...}, ASSERTION_SCRIPT: {...}, ... }
  const [debugResult, setDebugResult] = useState(null); // 当前选中脚本类型的调试结果
  const [executionLogs, setExecutionLogs] = useState([]);
  const [debugRuleDesc, setDebugRuleDesc] = useState(''); // 调试模式下的规则描述
  
  // 机审场景顺序调试相关状态
  const [debugOrder] = useState(['PRE_SCRIPT', 'ASSERTION_SCRIPT', 'METRIC_STATS_SCRIPT']); // 调试顺序
  const [completedDebugScripts, setCompletedDebugScripts] = useState([]); // 已完成调试的脚本类型
  const [tempMetricResult, setTempMetricResult] = useState(null); // 临时存储指标统计结果，用于结果统计页
  const [showTempStatsModal, setShowTempStatsModal] = useState(false); // 是否显示临时结果统计页弹窗
  
  // 指标统计脚本输出预览相关状态
  const [metricOutputPreviewData, setMetricOutputPreviewData] = useState(null); // 预览数据
  const [showMetricOutputPreview, setShowMetricOutputPreview] = useState(false); // 是否显示预览弹窗
  
  // 当前步骤：0-选择配置, 1-生成脚本, 2-调试验证
  const [currentStep, setCurrentStep] = useState(0);
  
  // AI打字效果
  const [typingContent, setTypingContent] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const typingRef = useRef(null);

  // 弹窗打开时初始化
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setDebugResults({}); // 清空所有脚本类型的调试结果
      setDebugResult(null);
      setExecutionLogs([]);
      setSelectedUseCaseId(null);
      setSelectedUseCase(null);
      setTypingContent('');
      setIsTyping(false);
      setCurrentScriptType(defaultScriptType || (scenarioCategory === 'AUTO_REVIEW' ? 'PRE_SCRIPT' : 'ASSERTION_SCRIPT'));
      setDebugRuleDesc(''); // 清空调试模式的规则描述
      
      // 重置一键生成进度
      setGenerateProgress({
        currentStep: '',
        statusMessage: '',
        isGenerating: false,
      });
      
      // 重置调试进度状态
      setCompletedDebugScripts([]);
      setTempMetricResult(null);
      setShowTempStatsModal(false);
      
      if (isDebugMode) {
        // 调试模式：直接进入调试步骤，使用表单中的脚本
        const formScripts = getFormScripts ? getFormScripts() : {};
        setScripts({
          PRE_SCRIPT: formScripts.preScript || '',
          ASSERTION_SERVICE_SCRIPT: formScripts.assertionPreScript || '',
          ASSERTION_SCRIPT: formScripts.assertionScript || '',
          METRIC_STATS_SCRIPT: formScripts.metricStatsScript || '',
        });
        setCurrentStep(2); // 直接跳到调试验证步骤
        // 设置默认选中的脚本类型
        setCurrentScriptType(defaultScriptType || (scenarioCategory === 'AUTO_REVIEW' ? 'PRE_SCRIPT' : 'ASSERTION_SCRIPT'));
      } else {
        // 生成模式：从第一步开始
        setScripts({
          PRE_SCRIPT: '',
          ASSERTION_SERVICE_SCRIPT: '',
          ASSERTION_SCRIPT: '',
          METRIC_STATS_SCRIPT: '',
        });
        setCurrentStep(0);
      }
      
      if (scenario?.id) {
        loadSampleUseCases(scenario.id);
      } else if (scenario?.businessScenarioId) {
        // 新增场景时，通过业务场景ID加载用例
        loadSampleUseCasesByBusinessScenario(scenario.businessScenarioId);
      }
    }
  }, [visible, scenario, isDebugMode]);

  // 检查是否有任何生成的脚本
  const hasAnyScript = scripts.PRE_SCRIPT || scripts.ASSERTION_SCRIPT ||
    scripts.ASSERTION_SERVICE_SCRIPT || scripts.METRIC_STATS_SCRIPT;

  // 加载样例用例
  const loadSampleUseCases = async (scenarioId) => {
    try {
      const result = await judgeScenarioApi.getSampleUseCases(scenarioId, 10);
      setSampleUseCases(result || []);
    } catch (error) {
      console.error('加载样例用例失败:', error);
    }
  };

  // 通过业务场景ID加载用例（新增场景时使用）
  const loadSampleUseCasesByBusinessScenario = async (businessScenarioId) => {
    try {
      // 使用 getFirstUseCase 接口获取一个用例作为样例
      const result = await judgeScenarioApi.getFirstUseCase({ businessScenarioId });
      if (result) {
        setSampleUseCases([result]);
      }
    } catch (error) {
      console.error('加载样例用例失败:', error);
    }
  };

  // 选择用例
  const handleSelectUseCase = (useCaseId) => {
    setSelectedUseCaseId(useCaseId);
    if (useCaseId && sampleUseCases.length > 0) {
      const uc = sampleUseCases.find(u => u.id === useCaseId);
      setSelectedUseCase(uc || null);
    } else {
      setSelectedUseCase(null);
    }
  };

  // 随机选择用例
  const handleRandomSelectUseCase = async () => {
    try {
      // 优先使用 scenarioId，如果没有则使用 businessScenarioId
      const params = scenario?.id 
        ? { scenarioId: scenario.id } 
        : { businessScenarioId: scenario?.businessScenarioId };
      const result = await judgeScenarioApi.getFirstUseCase(params);
      if (result) {
        setSelectedUseCaseId(result.id);
        setSelectedUseCase(result);
        message.success(`已选择用例: ${result.caseName}`);
      } else {
        message.warning('该场景下暂无用例');
      }
    } catch (error) {
      message.error('获取用例失败');
    }
  };

  // AI打字效果
  const typeContent = (content, callback) => {
    setIsTyping(true);
    let index = 0;
    const speed = 5;
    
    if (typingRef.current) {
      clearInterval(typingRef.current);
    }
    
    typingRef.current = setInterval(() => {
      if (index < content.length) {
        setTypingContent(content.substring(0, index + 1));
        index++;
      } else {
        clearInterval(typingRef.current);
        setIsTyping(false);
        if (callback) callback();
      }
    }, speed);
  };

  // 进入下一步（一键生成脚本）
  const handleNextToGenerate = async () => {
    // 机审场景：直接进入第1步生成脚本（跳过填写规则的步骤0）
    if (scenarioCategory === 'AUTO_REVIEW') {
      setCurrentStep(1);
      // 自动触发脚本生成
      setTimeout(() => {
        handleGenerateAllScripts();
      }, 100);
      return;
    }
    
    // 其他场景保持原有逻辑
    // 脚本生成描述非必填，直接进入下一步
    setCurrentStep(1);
  };

  // 返回上一步
  const handlePrevStep = () => {
    setCurrentStep(Math.max(0, currentStep - 1));
    setDebugResult(null);
  };

  // 一键生成所有脚本（异步）
  const handleGenerateAllScripts = async () => {
    try {
      setLoading(true);
      setGenerateProgress({
        currentStep: 'PRE_SCRIPT',
        statusMessage: '正在准备生成环境...',
        isGenerating: true,
      });
      
      // 从父组件表单获取已有的脚本
      const formScripts = getFormScripts ? getFormScripts() : {};
      const ruleDesc = form.getFieldValue('ruleDesc') || '';
      
      const request = {
        scenarioId: scenario?.id,
        businessScenarioId: scenario?.businessScenarioId,
        assertionRuleDesc: ruleDesc,
        randomSelectUseCase: true,
        serviceConfig: serviceConfig,
        scenarioCategory: scenarioCategory,
        // 传递表单中已有的脚本
        preScript: formScripts.preScript || '',
        assertionServiceScript: formScripts.assertionPreScript || '',
        metricConfig: formScripts.metricConfig || '',
        // 断言服务配置（要素场景）
        assertionServiceConfig: formScripts.assertionServiceConfig || '',
        // 结构化的启用指标/维度 key（供 AI 提示词注入）
        enabledPresetMetricKeys: formScripts.enabledPresetMetricKeys || [],
        enabledDimensionKeys: formScripts.enabledDimensionKeys || [],
        enabledCustomMetricCodes: formScripts.enabledCustomMetricCodes || [],
      };
      
      // 调用异步接口获取taskId
      const asyncResult = await judgeScenarioApi.aiGenerateAllScriptsAsync(request);
      const taskId = asyncResult.taskId;
      
      // 轮询任务状态
      const maxPollCount = 120; // 最多轮询120次（约4分钟）
      const pollInterval = 2000; // 每2秒轮询一次
      
      for (let i = 0; i < maxPollCount; i++) {
        const statusResult = await judgeScenarioApi.getAsyncTaskStatus(taskId);
        
        if (statusResult.status === 'SUCCESS') {
          const result = statusResult.result;
          
          // 更新所有生成的脚本
          setScripts(prev => ({
            ...prev,
            PRE_SCRIPT: result.generatedPreScript || prev.PRE_SCRIPT,
            ASSERTION_SERVICE_SCRIPT: result.generatedAssertionServiceScript || prev.ASSERTION_SERVICE_SCRIPT,
            ASSERTION_SCRIPT: result.generatedAssertionScript || prev.ASSERTION_SCRIPT,
            METRIC_STATS_SCRIPT: result.generatedMetricStatsScript || prev.METRIC_STATS_SCRIPT,
            METRIC_CONFIG_SCRIPT: result.generatedMetricConfigScript || prev.METRIC_CONFIG_SCRIPT,
          }));
          
          setGenerateProgress({
            currentStep: 'COMPLETED',
            statusMessage: '所有脚本生成完成！',
            isGenerating: false,
          });
          
          message.success('一键生成完成！');
          break;
        } else if (statusResult.status === 'FAILED') {
          throw new Error(statusResult.error || 'AI生成失败');
        } else if (statusResult.status === 'NOT_FOUND') {
          throw new Error('任务不存在或已过期');
        } else {
          // 状态为RUNNING，更新进度信息
          const runningResult = statusResult.result || {};
          setGenerateProgress({
            currentStep: runningResult.currentStep || 'RUNNING',
            statusMessage: runningResult.statusMessage || '正在生成中...',
            isGenerating: true,
          });
          
          // 如果有中间结果，实时更新
          if (runningResult.generatedPreScript) {
            setScripts(prev => ({ ...prev, PRE_SCRIPT: runningResult.generatedPreScript }));
          }
          if (runningResult.generatedAssertionServiceScript) {
            setScripts(prev => ({ ...prev, ASSERTION_SERVICE_SCRIPT: runningResult.generatedAssertionServiceScript }));
          }
          if (runningResult.generatedAssertionScript) {
            setScripts(prev => ({ ...prev, ASSERTION_SCRIPT: runningResult.generatedAssertionScript }));
          }
          if (runningResult.generatedMetricStatsScript) {
            setScripts(prev => ({ ...prev, METRIC_STATS_SCRIPT: runningResult.generatedMetricStatsScript }));
          }
          if (runningResult.generatedMetricConfigScript) {
            setScripts(prev => ({ ...prev, METRIC_CONFIG_SCRIPT: runningResult.generatedMetricConfigScript }));
          }
        }
        
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    } catch (error) {
      message.error('生成失败: ' + (error.message || '未知错误'));
      setGenerateProgress({
        currentStep: 'ERROR',
        statusMessage: '生成失败: ' + (error.message || '未知错误'),
        isGenerating: false,
      });
    } finally {
      setLoading(false);
    }
  };

  // 生成单个脚本（调试模式下使用）
  const handleGenerateScript = async () => {
    try {
      // 直接获取表单字段值（因为 ruleDesc 字段在步骤0中渲染，切换到步骤1后已不在DOM中）
      const ruleDesc = form.getFieldValue('ruleDesc');
      if (!ruleDesc || ruleDesc.trim() === '') {
        message.warning('请先填写脚本生成描述');
        setCurrentStep(0); // 返回步骤0填写
        return;
      }
      
      setLoading(true);
      setTypingContent('');
      
      const scriptTypeConfig = SCRIPT_TYPES.find(t => t.value === currentScriptType);
      
      // 从父组件表单获取已有的脚本（用于生成断言/后置脚本时，先执行前置脚本再调用服务）
      const formScripts = getFormScripts ? getFormScripts() : {};
      
      // 获取当前脚本类型对应的原脚本（编辑场景下传递给模型修改）
      const getCurrentScript = () => {
        switch (currentScriptType) {
          case 'PRE_SCRIPT': return formScripts.preScript || '';
          case 'POST_SCRIPT': return formScripts.postScript || '';
          case 'ASSERTION_SERVICE_SCRIPT': return formScripts.assertionPreScript || '';
          case 'ASSERTION_SCRIPT': return formScripts.assertionScript || '';
          case 'METRIC_STATS_SCRIPT': return formScripts.metricStatsScript || '';
          default: return '';
        }
      };
      
      const request = {
        scenarioId: scenario.id,
        businessScenarioId: scenario.businessScenarioId,
        scriptType: currentScriptType,
        assertionRuleDesc: ruleDesc,
        randomSelectUseCase: true, // 后端自动选择用例
        serviceConfig: serviceConfig,
        scenarioCategory: scenarioCategory,
        // 当前脚本类型的原脚本（编辑场景下传递给模型修改）
        currentScript: getCurrentScript(),
        // 传递表单中已有的脚本（用于调用服务获取返回值结构）
        preScript: formScripts.preScript || '',
        postScript: formScripts.postScript || '',
        assertionServiceScript: formScripts.assertionPreScript || '',
      };
      
      // 调用异步接口获取taskId
      const asyncResult = await judgeScenarioApi.aiGenerateScriptAsync(request);
      const taskId = asyncResult.taskId;
      
      // 轮询任务状态
      const pollTaskStatus = async () => {
        const maxPollCount = 60; // 最多轮询60次（约2分钟）
        const pollInterval = 2000; // 每2秒轮询一次
        
        for (let i = 0; i < maxPollCount; i++) {
          const statusResult = await judgeScenarioApi.getAsyncTaskStatus(taskId);
          
          if (statusResult.status === 'SUCCESS') {
            return statusResult.result;
          } else if (statusResult.status === 'FAILED') {
            throw new Error(statusResult.error || 'AI生成失败');
          } else if (statusResult.status === 'NOT_FOUND') {
            throw new Error('任务不存在或已过期');
          }
          // 状态为RUNNING，继续轮询
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
        throw new Error('生成超时，请重试');
      };
      
      const result = await pollTaskStatus();
      
      let generatedScript = '';
      if (currentScriptType === 'PRE_SCRIPT' && result.generatedPreScript) {
        generatedScript = result.generatedPreScript;
      } else if (result.generatedAssertionScript) {
        generatedScript = result.generatedAssertionScript;
      }
      
      if (generatedScript) {
        typeContent(generatedScript, () => {
          setScripts(prev => ({
            ...prev,
            [currentScriptType]: generatedScript
          }));
          message.success(`${scriptTypeConfig.label}生成成功！`);
        });
      } else {
        message.warning('AI未能生成有效脚本，请调整描述后重试');
      }
    } catch (error) {
      message.error('生成失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 进入查看结果步骤
  const handleNextToDebug = () => {
    const hasAnyScript = scripts.PRE_SCRIPT || scripts.ASSERTION_SCRIPT ||
      scripts.ASSERTION_SERVICE_SCRIPT || scripts.METRIC_STATS_SCRIPT || scripts.METRIC_CONFIG_SCRIPT;
    if (!hasAnyScript) {
      message.warning('请先生成脚本');
      return;
    }
    setCurrentStep(2);
    // 注意：不清空 debugResult 和 executionLogs，保留之前的调试结果
  };

  // 调试脚本
  const handleDebug = async () => {
    const currentScript = scripts[currentScriptType];
    if (!currentScript || currentScript.trim() === '') {
      message.warning('请先生成或输入脚本');
      return;
    }

    try {
      setDebugLoading(true);
      // 注意：不清空调试结果，保留之前各脚本的调试结果
      // setDebugResult(null);  // ❌ 移除清空逻辑
      // setExecutionLogs([]);  // ❌ 移除清空逻辑
      
      // 从父组件表单获取已有的脚本（用于补充弹窗内未生成的脚本）
      const formScripts = getFormScripts ? getFormScripts() : {};
      
      // 构建请求：优先使用弹窗内生成的脚本，其次使用父组件表单中的脚本
      const request = {
        scenarioId: scenario?.id,
        businessScenarioId: scenario?.businessScenarioId,
        scriptType: currentScriptType,
        preScript: scripts.PRE_SCRIPT || formScripts.preScript || '',
        assertionServiceScript: scripts.ASSERTION_SERVICE_SCRIPT || formScripts.assertionPreScript || '',
        assertionScript: scripts.ASSERTION_SCRIPT || formScripts.assertionScript || '',
        metricStatsScript: scripts.METRIC_STATS_SCRIPT || formScripts.metricStatsScript || '',
        metricConfig: formScripts.metricConfig || '',
        randomSelectUseCase: true, // 后端自动选择用例
        serviceConfig: serviceConfig,
      };

      const result = await judgeScenarioApi.aiDebugScript(request);
      
      // 保存调试结果到对应脚本类型
      const resultWithLogs = { ...result, executionLogs: result.executionLogs || [] };
      setDebugResults(prev => ({
        ...prev,
        [currentScriptType]: resultWithLogs
      }));
      setDebugResult(resultWithLogs);
      setExecutionLogs(resultWithLogs.executionLogs);
      
      // 机审场景：标记当前脚本为已调试完成
      if (scenarioCategory === 'AUTO_REVIEW' && !completedDebugScripts.includes(currentScriptType)) {
        setCompletedDebugScripts(prev => [...prev, currentScriptType]);
        
        // 如果是指标统计脚本，保存临时结果用于结果统计页
        if (currentScriptType === 'METRIC_STATS_SCRIPT' && resultWithLogs.metricStatsScriptOutput) {
          setTempMetricResult(resultWithLogs.metricStatsScriptOutput);
        }
        
        if (resultWithLogs.passed) {
          message.success(`${currentTypeConfig.label}调试通过！`);
        } else {
          message.warning(`${currentTypeConfig.label}调试未通过，请检查脚本`);
        }
      } else if (resultWithLogs.passed) {
        message.success('调试通过！');
      } else {
        message.warning('调试未通过，请检查脚本');
      }
    } catch (error) {
      message.error('调试失败: ' + (error.message || '未知错误'));
    } finally {
      setDebugLoading(false);
    }
  };

  // 一键调试所有脚本（异步）
  const handleDebugAll = async () => {
    const formScripts = getFormScripts ? getFormScripts() : {};
    const hasAssertionScript = scripts.ASSERTION_SCRIPT || formScripts.assertionScript;
    if (!hasAssertionScript) {
      message.warning('请先生成或输入断言脚本');
      return;
    }

    // 机审场景使用 METRIC_STATS_SCRIPT，其他场景使用 ASSERTION_SCRIPT
    const targetType = scenarioCategory === 'AUTO_REVIEW' ? 'METRIC_STATS_SCRIPT' : 'ASSERTION_SCRIPT';
    setCurrentScriptType(targetType);
    // 注意：不清空调试结果，保留之前各脚本的调试结果
    // setDebugResult(null);  // ❌ 移除清空逻辑
    // setExecutionLogs([]);  // ❌ 移除清空逻辑

    try {
      setDebugLoading(true);
      const request = {
        scenarioId: scenario?.id,
        businessScenarioId: scenario?.businessScenarioId,
        scriptType: targetType,
        preScript: scripts.PRE_SCRIPT || formScripts.preScript || '',
        assertionServiceScript: scripts.ASSERTION_SERVICE_SCRIPT || formScripts.assertionPreScript || '',
        assertionScript: scripts.ASSERTION_SCRIPT || formScripts.assertionScript || '',
        metricStatsScript: scripts.METRIC_STATS_SCRIPT || formScripts.metricStatsScript || '',
        metricConfig: formScripts.metricConfig || '',
        randomSelectUseCase: true,
        serviceConfig: serviceConfig,
      };

      // 调用异步接口获取taskId
      const asyncResult = await judgeScenarioApi.aiDebugScriptAsync(request);
      const taskId = asyncResult.taskId;
      
      // 轮询任务状态
      const maxPollCount = 60; // 最多轮询60次（约2分钟）
      const pollInterval = 2000; // 每2秒轮询一次
      
      for (let i = 0; i < maxPollCount; i++) {
        const statusResult = await judgeScenarioApi.getAsyncTaskStatus(taskId);
        
        if (statusResult.status === 'SUCCESS') {
          const result = statusResult.result;
          
          // 保存调试结果到对应脚本类型
          const resultWithLogs = { ...result, executionLogs: result.executionLogs || [] };
          setDebugResults(prev => ({
            ...prev,
            [targetType]: resultWithLogs
          }));
          setDebugResult(resultWithLogs);
          setExecutionLogs(resultWithLogs.executionLogs);
          
          if (resultWithLogs.passed) {
            message.success('一键调试通过！');
          } else {
            message.warning('调试未通过，请检查脚本');
          }
          break;
        } else if (statusResult.status === 'FAILED') {
          throw new Error(statusResult.error || '调试失败');
        } else if (statusResult.status === 'NOT_FOUND') {
          throw new Error('任务不存在或已过期');
        }
        // 状态为RUNNING，继续轮询
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    } catch (error) {
      message.error('调试失败: ' + (error.message || '未知错误'));
    } finally {
      setDebugLoading(false);
    }
  };

  // 应用脚本
  const handleApply = async () => {
    try {
      setConfirmLoading(true);
      
      if (onApply) {
        onApply({
          preScript: scripts.PRE_SCRIPT,
          assertionServiceScript: scripts.ASSERTION_SERVICE_SCRIPT,
          assertionScript: scripts.ASSERTION_SCRIPT,
          metricStatsScript: scripts.METRIC_STATS_SCRIPT,
        });
      }
      
      message.success('脚本已应用到表单');
      onCancel();
    } catch (error) {
      message.error('应用失败: ' + (error.message || '未知错误'));
    } finally {
      setConfirmLoading(false);
    }
  };

  // 调试模式下应用当前脚本到表单
  const handleApplyCurrentScript = () => {
    if (onApply) {
      // 只应用当前选中的脚本
      const applyData = {};
      if (currentScriptType === 'PRE_SCRIPT' && scripts.PRE_SCRIPT) {
        applyData.preScript = scripts.PRE_SCRIPT;
      }
      if (currentScriptType === 'ASSERTION_SERVICE_SCRIPT' && scripts.ASSERTION_SERVICE_SCRIPT) {
        applyData.assertionServiceScript = scripts.ASSERTION_SERVICE_SCRIPT;
      }
      if (currentScriptType === 'ASSERTION_SCRIPT' && scripts.ASSERTION_SCRIPT) {
        applyData.assertionScript = scripts.ASSERTION_SCRIPT;
      }
      if (currentScriptType === 'METRIC_STATS_SCRIPT' && scripts.METRIC_STATS_SCRIPT) {
        applyData.metricStatsScript = scripts.METRIC_STATS_SCRIPT;
      }
      onApply(applyData);
      message.success(`${currentTypeConfig.label}已应用到表单`);
      onCancel();
    }
  };

  // 调试模式下根据规则描述重新生成脚本（使用专用的 aiRegenerateScriptAsync 接口，支持错误上下文）
  const handleDebugRegenerate = async () => {
    if (!debugRuleDesc || debugRuleDesc.trim() === '') {
      message.warning('请先填写修复建议或规则描述');
      return;
    }
    
    try {
      setRegenerateLoading(true);
      setTypingContent('');
      
      const scriptTypeConfig = SCRIPT_TYPES.find(t => t.value === currentScriptType);
      
      // 构建请求，将错误信息和当前脚本作为修复上下文
      const request = {
        scenarioId: scenario?.id,
        businessScenarioId: scenario?.businessScenarioId,
        scriptType: currentScriptType,
        assertionRuleDesc: debugRuleDesc,
        // 传递错误信息供 AI 分析（字段名与后端对应）
        errorLog: debugResult?.error
          ? (typeof debugResult.error === 'string' ? debugResult.error : JSON.stringify(debugResult.error))
          : '',
        // 传递当前脚本，AI 在原脚本基础上修复（字段名与后端对应）
        originalScript: scripts[currentScriptType] || '',
        serviceConfig: serviceConfig,
        scenarioCategory: scenarioCategory,
      };
      
      // 调用专用的重新生成接口（不是普通生成接口）
      const asyncResult = await judgeScenarioApi.aiRegenerateScriptAsync(request);
      const taskId = asyncResult.taskId;
      
      // 轮询任务状态
      const maxPollCount = 60;
      const pollInterval = 2000;
      
      for (let i = 0; i < maxPollCount; i++) {
        const statusResult = await judgeScenarioApi.getAsyncTaskStatus(taskId);
        
        if (statusResult.status === 'SUCCESS') {
          const result = statusResult.result;
          // 解析重新生成的脚本（兼容多种返回格式）
          let generatedScript = '';
          if (typeof result === 'string' && result.trim()) {
            generatedScript = result;
          } else if (result && typeof result === 'object') {
            // 尝试从对象中取对应脚本字段
            const fieldMap = {
              'PRE_SCRIPT': 'generatedPreScript',
              'ASSERTION_SCRIPT': 'generatedAssertionScript',
              'ASSERTION_SERVICE_SCRIPT': 'generatedAssertionServiceScript',
              'METRIC_STATS_SCRIPT': 'generatedMetricStatsScript',
            };
            generatedScript = result[fieldMap[currentScriptType]] || '';
          }
          
          if (generatedScript) {
            setScripts(prev => ({ ...prev, [currentScriptType]: generatedScript }));
            // 清空当前脚本类型的调试结果（因为脚本已更新）
            setDebugResults(prev => {
              const newResults = { ...prev };
              delete newResults[currentScriptType];
              return newResults;
            });
            setDebugResult(null);
            message.success(`${scriptTypeConfig?.label || '脚本'}重新生成成功，请再次点击开始调试验证`);
          } else {
            message.warning('AI未能生成有效脚本，请调整描述后重试');
          }
          break;
        } else if (statusResult.status === 'FAILED') {
          throw new Error(statusResult.error || 'AI生成失败');
        } else if (statusResult.status === 'NOT_FOUND') {
          throw new Error('任务不存在或已过期');
        }
        
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    } catch (error) {
      message.error('重新生成失败: ' + (error.message || '未知错误'));
    } finally {
      setRegenerateLoading(false);
    }
  };

  // 渲染错误阶段标签
  const renderErrorStageTag = (stage) => {
    const stageMap = {
      'PRE_SCRIPT': { color: 'orange', text: '前置脚本' },
      'SERVICE_CALL': { color: 'purple', text: '服务调用' },
      'ASSERTION_SERVICE': { color: 'geekblue', text: '断言服务' },
      'ASSERTION_SCRIPT': { color: 'red', text: '断言脚本' },
      'METRIC_CONFIG_SCRIPT': { color: 'magenta', text: '自定义指标脚本' },
      'METRIC_STATS_SCRIPT': { color: 'cyan', text: '指标统计脚本' },
    };
    const config = stageMap[stage] || { color: 'default', text: stage };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 渲染脚本输出卡片
  // 渲染输出卡片（带复制按钮）
  const renderOutputCard = (title, output, success, showPreviewButton = false, onPreview = null) => {
    if (output === null || output === undefined) return null;
    
    const outputText = typeof output === 'string' ? output : JSON.stringify(output, null, 2);
    
    return (
      <Card 
        size="small" 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Space>
              {success === true ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : 
               success === false ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> :
               <CaretRightOutlined />}
              <Text strong>{title}</Text>
              {success !== null && (
                <Tag color={success ? 'success' : 'error'}>
                  {success ? '成功' : '失败'}
                </Tag>
              )}
            </Space>
            <Space>
              {showPreviewButton && onPreview && (
                <Button
                  size="small"
                  type="primary"
                  icon={<BarChartOutlined />}
                  onClick={onPreview}
                >
                  查看统计结果页
                </Button>
              )}
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(outputText).then(() => {
                    message.success('输出值已复制到剪贴板');
                  }).catch(() => {
                    message.error('复制失败，请手动复制');
                  });
                }}
              >
                复制
              </Button>
            </Space>
          </div>
        }
        style={{ marginBottom: 12 }}
      >
        <pre style={{ 
          background: '#f6f8fa', 
          padding: 12, 
          borderRadius: 6, 
          maxHeight: 200, 
          overflow: 'auto', 
          fontSize: 12,
          margin: 0,
        }}>
          {outputText}
        </pre>
      </Card>
    );
  };

  // 获取当前脚本类型配置
  const currentTypeConfig = SCRIPT_TYPES.find(t => t.value === currentScriptType) || SCRIPT_TYPES[3];

  // 是否有已生成的脚本
  const hasGeneratedScript = scripts[currentScriptType] && scripts[currentScriptType].trim() !== '';

  // 获取当前场景可用的脚本类型
  const getAvailableScriptTypes = () => {
    return SCRIPT_TYPES.filter(item => {
      // 如果没有指定场景类别限制，或者当前场景匹配，则显示
      if (!item.scenarioCategory) return true;
      return item.scenarioCategory === scenarioCategory;
    });
  };

  // 按类别分组脚本类型
  const getScriptTypesByCategory = () => {
    const available = getAvailableScriptTypes();
    return {
      common: available.filter(item => item.category === 'common'),
      special: available.filter(item => item.category !== 'common'),
    };
  };

  // 渲染步骤0：配置参数（简化版，移除脚本类型选择）
  const renderStep0 = () => {
    // 机审场景：直接显示一键生成按钮，不需要规则描述
    if (scenarioCategory === 'AUTO_REVIEW') {
      return (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="一键生成模式"
            description={
              <div>
                <p style={{ margin: '8px 0' }}>
                  点击“开始生成脚本”按钮后，AI将自动生成以下脚本：
                </p>
                <ol style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
                  <li>前置脚本：准备服务调用参数</li>
                  <li>断言脚本：验证服务响应</li>
                  <li>指标统计脚本：计算评测指标</li>
                </ol>
              </div>
            }
          />
        </>
      );
    }
      
    // 其他场景保持原有逻辑
    return (
      <>
        {/* 一键生成说明 */}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="一键生成模式"
          description={
            <div>
              <p style={{ margin: '8px 0' }}>
                根据场景类型自动生成所有相关脚本：
              </p>
              {scenarioCategory === 'AUTO_REVIEW' ? (
                <ol style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
                  <li>前置脚本：准备服务调用参数</li>
                  <li>断言脚本：验证服务响应</li>
                  <li>指标统计脚本：计算评测指标</li>
                  <li>自定义指标脚本（可选）：计算自定义指标</li>
                </ol>
              ) : (
                <ol style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
                  <li>前置脚本：准备服务调用参数</li>
                  <li>断言服务脚本（可选）：调用断言服务</li>
                  <li>断言脚本：验证服务响应</li>
                  <li>指标统计脚本：计算评测指标</li>
                </ol>
              )}
            </div>
          }
        />
  
        {/* 规则描述（非必填） */}
        <Form form={form} layout="vertical">
          <Form.Item
            name="ruleDesc"
            label="脚本生成描述（可选）"
            extra="描述脚本需要实现的逻辑，如：数据转换规则、断言判断条件等，可帮助AI更好地生成脚本"
          >
            <TextArea
              rows={4}
              placeholder="例如：前置脚本需要将useCase.documentId作为请求参数；断言脚本需要比对serviceResponse.riskLevel与useCase.expectedAnswer中的结果是否一致..."
            />
          </Form.Item>
        </Form>
      </>
    );
  };

  // 渲染步骤1：一键生成所有脚本
  const renderStep1 = () => {
    // 获取当前场景需要生成的脚本类型列表
    const getScriptSteps = () => {
      if (scenarioCategory === 'AUTO_REVIEW') {
        return [
          { key: 'PRE_SCRIPT', label: '前置脚本', icon: '1️⃣' },
          { key: 'ASSERTION_SCRIPT', label: '断言脚本', icon: '2️⃣' },
          { key: 'METRIC_STATS_SCRIPT', label: '指标统计脚本', icon: '3️⃣' },
        ];
      } else {
        return [
          { key: 'PRE_SCRIPT', label: '前置脚本', icon: '1️⃣' },
          { key: 'ASSERTION_SERVICE_SCRIPT', label: '断言服务脚本', icon: '2️⃣', optional: true },
          { key: 'ASSERTION_SCRIPT', label: '断言脚本', icon: '3️⃣' },
          { key: 'METRIC_STATS_SCRIPT', label: '指标统计脚本', icon: '4️⃣' },
        ];
      }
    };
    
    const scriptSteps = getScriptSteps();
    
    // 获取当前步骤的索引
    const getCurrentStepIndex = () => {
      const stepKey = generateProgress.currentStep;
      const idx = scriptSteps.findIndex(s => s.key === stepKey);
      return idx >= 0 ? idx : (generateProgress.isGenerating ? 0 : -1);
    };
    
    return (
      <>
        {/* 生成进度显示 */}
        {generateProgress.isGenerating && (
          <Card size="small" style={{ marginBottom: 16, background: '#f0f5ff', borderColor: '#adc6ff' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <RobotOutlined spin style={{ color: '#1890ff', fontSize: 18 }} />
                <Text strong>AI 正在生成脚本...</Text>
              </Space>
              <div style={{ marginTop: 8 }}>
                {scriptSteps.map((step, idx) => {
                  const currentIdx = getCurrentStepIndex();
                  const isCompleted = idx < currentIdx || (generateProgress.currentStep === 'COMPLETED');
                  const isCurrent = idx === currentIdx && generateProgress.currentStep !== 'COMPLETED';
                  const hasScript = scripts[step.key];
                  
                  return (
                    <div key={step.key} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '4px 0',
                      color: isCurrent ? '#1890ff' : (isCompleted || hasScript ? '#52c41a' : '#999')
                    }}>
                      <span style={{ marginRight: 8 }}>{step.icon}</span>
                      <span>{step.label}</span>
                      {step.optional && <Tag style={{ marginLeft: 8 }} color="default">可选</Tag>}
                      {(isCompleted || hasScript) && <CheckCircleOutlined style={{ marginLeft: 8, color: '#52c41a' }} />}
                      {isCurrent && <SyncOutlined spin style={{ marginLeft: 8 }} />}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 8, padding: 8, background: '#fff', borderRadius: 4 }}>
                <Text type="secondary">{generateProgress.statusMessage || '正在准备...'}</Text>
              </div>
            </Space>
          </Card>
        )}

        {/* 生成完成后显示所有脚本 - 使用更优美的AI风格卡片布局 */}
        {!generateProgress.isGenerating && hasAnyScript && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: 16,
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 12,
              color: '#fff'
            }}>
              <RobotOutlined style={{ fontSize: 24, marginRight: 12 }} />
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>AI 脚本生成完成</div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>所有脚本已生成，您可以直接应用或编辑后使用</div>
              </div>
            </div>
            
            <div style={{ display: 'grid', gap: 12 }}>
              {scripts.PRE_SCRIPT && (
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  border: '1px solid #e8e8e8',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: 'linear-gradient(90deg, #f0f5ff 0%, #e6f7ff 100%)',
                    borderBottom: '1px solid #e8e8e8'
                  }}>
                    <Space>
                      <span style={{ fontSize: 20 }}>⚡</span>
                      <Text strong style={{ color: '#1890ff' }}>前置脚本</Text>
                      <Tag color="success" size="small">已生成</Tag>
                    </Space>
                    <Button 
                      size="small" 
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(scripts.PRE_SCRIPT);
                        message.success('已复制到剪贴板');
                      }}
                    >
                      复制
                    </Button>
                  </div>
                  <div style={{ padding: 12 }}>
                    <CodeEditor
                      key="PRE_SCRIPT"
                      value={scripts.PRE_SCRIPT}
                      onChange={(val) => setScripts(prev => ({ ...prev, PRE_SCRIPT: val }))}
                      defaultHeight={120}
                    />
                  </div>
                </div>
              )}
              
              {scripts.ASSERTION_SERVICE_SCRIPT && (
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  border: '1px solid #e8e8e8',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: 'linear-gradient(90deg, #f6ffed 0%, #f0f5ff 100%)',
                    borderBottom: '1px solid #e8e8e8'
                  }}>
                    <Space>
                      <span style={{ fontSize: 20 }}>🔍</span>
                      <Text strong style={{ color: '#52c41a' }}>断言服务脚本</Text>
                      <Tag color="success" size="small">已生成</Tag>
                    </Space>
                    <Button 
                      size="small" 
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(scripts.ASSERTION_SERVICE_SCRIPT);
                        message.success('已复制到剪贴板');
                      }}
                    >
                      复制
                    </Button>
                  </div>
                  <div style={{ padding: 12 }}>
                    <CodeEditor
                      key="ASSERTION_SERVICE_SCRIPT"
                      value={scripts.ASSERTION_SERVICE_SCRIPT}
                      onChange={(val) => setScripts(prev => ({ ...prev, ASSERTION_SERVICE_SCRIPT: val }))}
                      defaultHeight={120}
                    />
                  </div>
                </div>
              )}
              
              {scripts.ASSERTION_SCRIPT && (
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  border: '1px solid #e8e8e8',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: 'linear-gradient(90deg, #fff7e6 0%, #fff1f0 100%)',
                    borderBottom: '1px solid #e8e8e8'
                  }}>
                    <Space>
                      <span style={{ fontSize: 20 }}>✅</span>
                      <Text strong style={{ color: '#fa8c16' }}>断言脚本</Text>
                      <Tag color="success" size="small">已生成</Tag>
                    </Space>
                    <Button 
                      size="small" 
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(scripts.ASSERTION_SCRIPT);
                        message.success('已复制到剪贴板');
                      }}
                    >
                      复制
                    </Button>
                  </div>
                  <div style={{ padding: 12 }}>
                    <CodeEditor
                      key="ASSERTION_SCRIPT"
                      value={scripts.ASSERTION_SCRIPT}
                      onChange={(val) => setScripts(prev => ({ ...prev, ASSERTION_SCRIPT: val }))}
                      defaultHeight={150}
                    />
                  </div>
                </div>
              )}
              
              {scripts.METRIC_STATS_SCRIPT && (
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  border: '1px solid #e8e8e8',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: 'linear-gradient(90deg, #f9f0ff 0%, #f0f5ff 100%)',
                    borderBottom: '1px solid #e8e8e8'
                  }}>
                    <Space>
                      <span style={{ fontSize: 20 }}>📊</span>
                      <Text strong style={{ color: '#722ed1' }}>指标统计脚本</Text>
                      <Tag color="success" size="small">已生成</Tag>
                    </Space>
                    <Button 
                      size="small" 
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(scripts.METRIC_STATS_SCRIPT);
                        message.success('已复制到剪贴板');
                      }}
                    >
                      复制
                    </Button>
                  </div>
                  <div style={{ padding: 12 }}>
                    <CodeEditor
                      key="METRIC_STATS_SCRIPT"
                      value={scripts.METRIC_STATS_SCRIPT}
                      onChange={(val) => setScripts(prev => ({ ...prev, METRIC_STATS_SCRIPT: val }))}
                      defaultHeight={120}
                    />
                  </div>
                </div>
              )}
              
              {scripts.METRIC_CONFIG_SCRIPT && (
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  border: '1px solid #e8e8e8',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: 'linear-gradient(90deg, #e6fffb 0%, #f6ffed 100%)',
                    borderBottom: '1px solid #e8e8e8'
                  }}>
                    <Space>
                      <span style={{ fontSize: 20 }}>🎯</span>
                      <Text strong style={{ color: '#13c2c2' }}>自定义指标脚本</Text>
                      <Tag color="success" size="small">已生成</Tag>
                    </Space>
                    <Button 
                      size="small" 
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(scripts.METRIC_CONFIG_SCRIPT);
                        message.success('已复制到剪贴板');
                      }}
                    >
                      复制
                    </Button>
                  </div>
                  <div style={{ padding: 12 }}>
                    <CodeEditor
                      key="METRIC_CONFIG_SCRIPT"
                      value={scripts.METRIC_CONFIG_SCRIPT}
                      onChange={(val) => setScripts(prev => ({ ...prev, METRIC_CONFIG_SCRIPT: val }))}
                      defaultHeight={120}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  // 渲染步骤2：调试验证（重构版 - 支持 generate 和 debug 两种模式）
  const renderStep2 = () => {
    // 机审场景：只显示3个脚本类型，按顺序调试
    const availableScriptTypes = scenarioCategory === 'AUTO_REVIEW' 
      ? SCRIPT_TYPES.filter(item => ['PRE_SCRIPT', 'ASSERTION_SCRIPT', 'METRIC_STATS_SCRIPT'].includes(item.value))
      : SCRIPT_TYPES.filter(item => {
          if (!item.scenarioCategory) return true;
          return item.scenarioCategory === scenarioCategory;
        });
      
    // 获取当前应该调试的脚本（第一个未完成的）
    const currentDebugIndex = completedDebugScripts.length;
    const currentShouldDebugScript = currentDebugIndex < debugOrder.length 
      ? debugOrder[currentDebugIndex] 
      : null;
    
    return (
      <>
        {/* 脚本类型选择 - 机审场景3个卡片横向占满一行 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {scenarioCategory === 'AUTO_REVIEW' ? '按顺序调试脚本' : '选择脚本类型'}
            </Text>
            {scenarioCategory === 'AUTO_REVIEW' && (
              <Alert 
                message="请按顺序调试：前置脚本 → 断言脚本 → 指标统计脚本" 
                type="info" 
                showIcon 
                style={{ marginBottom: 12 }}
              />
            )}
              
            {/* 机审场景：脚本类型卡片横向排列，占满一行 */}
            {scenarioCategory === 'AUTO_REVIEW' ? (
              <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                {availableScriptTypes.map(item => {
                  const isSelected = currentScriptType === item.value;
                  const isCompleted = completedDebugScripts.includes(item.value);
                  const isCurrentTarget = currentShouldDebugScript === item.value;
                  const isDisabled = !isCurrentTarget && !isCompleted;
                    
                  return (
                    <div
                      key={item.value}
                      onClick={() => {
                        if (!isDisabled) {
                          setCurrentScriptType(item.value);
                          if (debugResults[item.value]) {
                            setDebugResult(debugResults[item.value]);
                            setExecutionLogs(debugResults[item.value].executionLogs || []);
                          }
                        }
                      }}
                      style={{
                        flex: 1,
                        border: `2px solid ${isSelected ? '#1677ff' : '#e8e8e8'}`,
                        borderRadius: 8,
                        background: isSelected ? '#e6f4ff' : (isCompleted ? '#f6ffed' : '#fff'),
                        opacity: isDisabled ? 0.5 : 1,
                        transition: 'all 0.2s',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        padding: '16px 12px',
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ marginBottom: 8 }}>
                        <Text strong style={{ fontSize: 14, color: isSelected ? '#1677ff' : '#262626' }}>
                          {item.label}
                        </Text>
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        {isCompleted && <Tag color="success">已调试</Tag>}
                        {isCurrentTarget && !isCompleted && <Badge status="processing" text="待调试" />}
                        {!isCurrentTarget && !isCompleted && <Tag color="default">未开始</Tag>}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{item.desc}</Text>
                    </div>
                  );
                })}
              </div>
            ) : (
              // 非机审场景：纵向排列
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                {availableScriptTypes.map(item => {
                  const isSelected = currentScriptType === item.value;
                  const isCompleted = completedDebugScripts.includes(item.value);
                  const isCurrentTarget = currentShouldDebugScript === item.value;
                  const isDisabled = scenarioCategory === 'AUTO_REVIEW' && !isCurrentTarget && !isCompleted;
                  const hasScript = scripts[item.value] && scripts[item.value].trim();
                    
                  return (
                    <div
                      key={item.value}
                      onClick={() => {
                        if (!isDisabled) {
                          setCurrentScriptType(item.value);
                          if (debugResults[item.value]) {
                            setDebugResult(debugResults[item.value]);
                            setExecutionLogs(debugResults[item.value].executionLogs || []);
                          }
                        }
                      }}
                      style={{
                        border: `2px solid ${isSelected ? '#1677ff' : '#e8e8e8'}`,
                        borderRadius: 8,
                        background: isSelected ? '#e6f4ff' : (isCompleted ? '#f6ffed' : '#fff'),
                        opacity: isDisabled ? 0.5 : 1,
                        transition: 'all 0.2s',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      {/* 左侧：图标 + 标题 + 描述 */}
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                          <Text strong style={{ fontSize: 14, color: isSelected ? '#1677ff' : '#262626', marginRight: 8 }}>
                            {item.label}
                          </Text>
                          {isCompleted && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 14 }} />}
                          {isCurrentTarget && !isCompleted && <Badge status="processing" />}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>{item.desc}</Text>
                      </div>
                        
                      {/* 右侧：状态标签 */}
                      <div style={{ marginLeft: 16 }}>
                        {isCompleted ? (
                          <Tag color="success">已调试</Tag>
                        ) : isCurrentTarget ? (
                          <Tag color="processing">待调试</Tag>
                        ) : (
                          <Tag color="default">未开始</Tag>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
    
        {/* 脚本编辑器 - 两种模式都可编辑 */}
        <Card
          size="small"
          title={
            <Space>
              <Text strong>{currentTypeConfig.label}</Text>
              <Tag color="blue">可编辑</Tag>
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          <div style={{ border: '1px solid #d9d9d9', borderRadius: 6 }}>
            <CodeEditor
              key={currentScriptType}
              value={scripts[currentScriptType]}
              onChange={(val) => setScripts(prev => ({ ...prev, [currentScriptType]: val }))}
              defaultHeight={200}
            />
          </div>
        </Card>
          
        {/* 调试操作区 - 底部，机审场景移除调试按钮（已移至弹窗底部） */}
        {scenarioCategory !== 'AUTO_REVIEW' && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* 调试按钮 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  提示：调试将使用样例用例验证脚本执行结果
                </Text>
                <Button
                  type="primary"
                  icon={<BugOutlined />}
                  onClick={() => handleDebug()}
                  loading={debugLoading}
                  disabled={!scripts[currentScriptType] || !scripts[currentScriptType].trim()}
                >
                  开始调试{currentTypeConfig.label}
                </Button>
              </div>
            </Space>
          </Card>
        )}
  
        {/* 调试结果 - 只要有任何脚本被调试过就显示 */}
        {(debugResult || Object.keys(debugResults).length > 0) && (
          <Card
            size="small"
            title={
              <Space>
                {!debugResult?.error ?
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} /> :
                  <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
                }
                <Text strong>调试结果</Text>
                {debugResult ? (
                  !debugResult.error ? (
                    <Tag color="success">脚本执行成功</Tag>
                  ) : (
                    <Tag color="error">脚本执行失败</Tag>
                  )
                ) : (
                  <Tag color="default">已调试</Tag>
                )}
                {debugResult?.errorStage && renderErrorStageTag(debugResult.errorStage)}
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            {/* 用例信息 */}
            {debugResult?.useCaseName && (
              <div className="mb-4">
                <div className="font-medium mb-2">调试用例：</div>
                <div className="bg-blue-50 p-3 rounded text-sm border border-blue-200">
                  <div className="mb-1">
                    <span className="text-gray-600">用例名称：</span>
                    <span className="font-medium">{debugResult.useCaseName}</span>
                  </div>
                  {debugResult.useCaseId && (
                    <div className="mb-1">
                      <span className="text-gray-600">用例ID：</span>
                      <span className="font-mono">{debugResult.useCaseId}</span>
                    </div>
                  )}
                  {debugResult.expectedAnswer && (
                    <div>
                      <span className="text-gray-600">期望答案：</span>
                      <pre className="mt-1 bg-white p-2 rounded text-xs overflow-auto max-h-40 border border-blue-100" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {typeof debugResult.expectedAnswer === 'string' 
                          ? debugResult.expectedAnswer 
                          : JSON.stringify(debugResult.expectedAnswer, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 执行日志 */}
            {executionLogs && executionLogs.length > 0 && (
              <div style={{ marginBottom: 12, padding: 8, background: '#fafafa', borderRadius: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>执行日志:</Text>
                <div style={{ marginTop: 4 }}>
                  {executionLogs.map((log, idx) => (
                    <div key={idx} style={{ fontSize: 12, color: '#666' }}>{log}</div>
                  ))}
                </div>
              </div>
            )}
  
            {/* 各阶段输出 */}
            {debugResult && (
              <>
                {renderOutputCard('前置脚本输出', debugResult.preScriptOutput, debugResult.preScriptSuccess)}
                {renderOutputCard('服务响应', debugResult.serviceResponse, debugResult.serviceCallSuccess)}
                {renderOutputCard('断言服务输出', debugResult.assertionServiceOutput, debugResult.assertionServiceSuccess)}
                {renderOutputCard('断言脚本输出', debugResult.assertionOutput, debugResult.assertionScriptSuccess)}
                {renderOutputCard(
                  '指标统计脚本输出', 
                  debugResult.metricStatsScriptOutput, 
                  debugResult.metricStatsScriptSuccess,
                  true, // 显示预览按钮
                  () => {
                    // 打开全屏弹窗预览
                    const outputData = debugResult.metricStatsScriptOutput;
                    if (outputData) {
                      try {
                        // 如果是字符串，尝试解析
                        const parsedData = typeof outputData === 'string' ? JSON.parse(outputData) : outputData;
                        setMetricOutputPreviewData(parsedData);
                        setShowMetricOutputPreview(true);
                      } catch (e) {
                        message.error('输出数据格式错误，无法预览');
                      }
                    }
                  }
                )}
              </>
            )}
  
            {/* 脚本执行上下文 */}
            {debugResult?.scriptContext && (
              <Collapse size="small" style={{ marginBottom: 12 }}>
                <Panel header="脚本执行上下文" key="context">
                  <pre style={{ background: '#f6f8fa', padding: 12, borderRadius: 6, maxHeight: 200, overflow: 'auto', fontSize: 12, margin: 0 }}>
                    {JSON.stringify(debugResult.scriptContext, null, 2)}
                  </pre>
                </Panel>
              </Collapse>
            )}
  
            {/* 错误信息 */}
            {debugResult?.error && (
              <Alert
                type="error"
                message="错误信息"
                description={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{debugResult.error}</pre>}
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}
  
            {/* AI重新生成脚本区域 - 重新设计交互 */}
            {debugResult && (
              <div style={{ marginTop: 16 }}>
                <Divider style={{ margin: '16px 0 12px' }}>
                  <Space>
                    <RobotOutlined style={{ color: '#1677ff' }} />
                    <Text strong>AI重新生成脚本</Text>
                  </Space>
                </Divider>
                
                <Card size="small" style={{ background: '#fafafa', border: '1px solid #e8e8e8' }}>
                  <div style={{ marginBottom: 12 }}>
                    <Space>
                      <Text type="secondary">当前脚本：</Text>
                      <Tag color="blue">{currentTypeConfig.label}</Tag>
                      {!debugResult.error ? (
                        <Tag color="success">✓ 调试通过</Tag>
                      ) : (
                        <Tag color="error">✗ 调试失败</Tag>
                      )}
                    </Space>
                  </div>
                  
                  <div style={{ marginBottom: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      {debugResult.error 
                        ? '描述修复建议，AI将根据错误信息重新生成脚本' 
                        : '描述您期望的脚本逻辑，AI将重新生成'
                      }
                    </Text>
                    <Input.TextArea
                      value={debugRuleDesc}
                      onChange={(e) => setDebugRuleDesc(e.target.value)}
                      placeholder={debugResult.error 
                        ? "例如：请修复变量未定义的错误，增加对ruleDetails的校验..." 
                        : "例如：请修改断言逻辑，增加对XX字段的校验..."
                      }
                      rows={3}
                      style={{ marginBottom: 8 }}
                    />
                  </div>
                  
                  <Space>
                    {debugResult.error && (
                      <Button
                        size="small"
                        icon={<BugOutlined />}
                        onClick={() => {
                          const errorStr = typeof debugResult.error === 'string'
                            ? debugResult.error
                            : JSON.stringify(debugResult.error);
                          setDebugRuleDesc(`请修复以下错误：${errorStr}`);
                          message.info('已将错误信息填充到修复建议区域');
                        }}
                      >
                        自动填充错误
                      </Button>
                    )}
                    <Button
                      type="primary"
                      size="small"
                      icon={<RobotOutlined />}
                      onClick={handleDebugRegenerate}
                      loading={regenerateLoading}
                      disabled={!debugRuleDesc || !debugRuleDesc.trim()}
                    >
                      AI重新生成{currentTypeConfig.label}
                    </Button>
                  </Space>
                </Card>
              </div>
            )}
          </Card>
        )}
      </>
    );
  };

  // 渲染底部按钮（固定在弹窗底部）
  const renderFooter = () => {
    // 调试模式
    if (isDebugMode) {
      return (
        <Space>
          <Button onClick={onCancel}>关闭</Button>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleApplyCurrentScript}
          >
            应用到表单
          </Button>
        </Space>
      );
    }

    // 生成模式：根据步骤显示不同按钮
    switch (currentStep) {
      case 0:
        // 机审场景：跳过配置参数步骤，直接开始生成
        if (scenarioCategory === 'AUTO_REVIEW') {
          return (
            <Space>
              <Button onClick={onCancel}>取消</Button>
              <Button
                type="primary"
                icon={<RobotOutlined />}
                onClick={handleNextToGenerate}
              >
                生成脚本
              </Button>
            </Space>
          );
        }
        // 非机审场景：显示配置参数步骤
        return (
          <Space>
            <Button onClick={onCancel}>取消</Button>
            <Button
              type="primary"
              icon={<RobotOutlined />}
              onClick={handleNextToGenerate}
            >
              开始生成脚本
            </Button>
          </Space>
        );
      case 1:
        return (
          <Space>
            <Button onClick={handlePrevStep} disabled={generateProgress.isGenerating}>上一步</Button>
            {!hasAnyScript ? (
              <Button
                type="primary"
                icon={<RobotOutlined />}
                onClick={handleGenerateAllScripts}
                loading={loading}
              >
                一键生成所有脚本
              </Button>
            ) : (
              // 机审场景：生成后直接显示调试按钮，不显示重新生成
              scenarioCategory === 'AUTO_REVIEW' ? (
                <Button
                  type="primary"
                  icon={<BugOutlined />}
                  onClick={() => { 
                    setCurrentStep(2); 
                    setDebugResult(null);
                    // 机审场景默认选中前置脚本
                    setCurrentScriptType('PRE_SCRIPT');
                  }}
                  disabled={generateProgress.isGenerating}
                >
                  调试脚本
                </Button>
              ) : (
                <Space>
                  <Button
                    icon={<SyncOutlined />}
                    onClick={handleGenerateAllScripts}
                    loading={loading}
                    disabled={generateProgress.isGenerating}
                  >
                    重新生成
                  </Button>
                  <Button
                    type="primary"
                    icon={<BugOutlined />}
                    onClick={() => { 
                      setCurrentStep(2); 
                      setDebugResult(null);
                      // 非机审场景默认选中断言脚本
                      setCurrentScriptType('ASSERTION_SCRIPT');
                    }}
                    disabled={generateProgress.isGenerating}
                  >
                    下一步（调试验证）
                  </Button>
                </Space>
              )
            )}
          </Space>
        );
      case 2:
      default:
        // 机审场景：检查是否所有脚本都调试完成
        const allDebugCompleted = scenarioCategory === 'AUTO_REVIEW' 
          && completedDebugScripts.length === 3;
        
        return (
          <Space>
            <Button onClick={handlePrevStep}>上一步</Button>
            {
              scenarioCategory !== 'AUTO_REVIEW' && (
                <Button
                  icon={<BugOutlined />}
                  onClick={handleDebugAll}
                  loading={debugLoading}
                  disabled={!hasAnyScript}
                >
                  一键调试所有脚本
                </Button>
              )
            }
            {/* 机审场景：调试按钮放在弹窗底部 */}
            {scenarioCategory === 'AUTO_REVIEW' && (
              <Button
                type="primary"
                icon={<BugOutlined />}
                onClick={() => handleDebug()}
                loading={debugLoading}
                disabled={!scripts[currentScriptType] || !scripts[currentScriptType].trim()}
              >
                调试{currentTypeConfig.label}
              </Button>
            )}
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleApply}
              loading={confirmLoading}
              disabled={scenarioCategory === 'AUTO_REVIEW' && !allDebugCompleted}
            >
              {scenarioCategory === 'AUTO_REVIEW' 
                ? (allDebugCompleted ? '应用所有脚本到表单' : `还需调试 ${3 - completedDebugScripts.length} 个脚本`)
                : '应用所有脚本'
              }
            </Button>
          </Space>
        );
    }
  };

  return (
    <>
      <Modal
        title={
          <Space>
            {isDebugMode ? <BugOutlined style={{ color: '#1890ff' }} /> : <RobotOutlined style={{ color: '#1890ff' }} />}
            <span>{isDebugMode ? '脚本调试' : 'AI脚本生成助手'}</span>
            {scenarioCategory && (
              <Tag color="blue">{SCENARIO_CATEGORIES[scenarioCategory] || scenarioCategory}</Tag>
            )}
          </Space>
        }
        open={visible}
        onCancel={onCancel}
        footer={renderFooter()}
        width={1000}
        centered
        bodyStyle={{ minHeight: 500, maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}
        destroyOnClose
      >
        <Spin spinning={loading && !generateProgress.isGenerating} tip="AI正在生成脚本...">
          {/* 步骤进度条 - 调试模式不显示 */}
          {!isDebugMode && (
            <>
              {/* 非机审场景：显示3步 */}
              {scenarioCategory !== 'AUTO_REVIEW' && (
                <Steps
                  current={currentStep}
                  size="small"
                  style={{ marginBottom: 24 }}
                  items={[
                    { title: '配置参数', description: '填写生成描述' },
                    { title: '生成脚本', description: 'AI生成所有脚本' },
                    { title: '调试验证', description: '验证脚本并应用' },
                  ]}
                />
              )}
              {/* 机审场景：显示2步 */}
              {scenarioCategory === 'AUTO_REVIEW' && (
                <Steps
                  current={currentStep - 1}  // 机审场景从步骤1开始，所以减1
                  size="small"
                  style={{ marginBottom: 24 }}
                  items={[
                    { title: '生成脚本', description: 'AI生成所有脚本' },
                    { title: '调试验证', description: '验证脚本并应用' },
                  ]}
                />
              )}
            </>
          )}

          {/* 根据步骤渲染内容 */}
          {currentStep === 0 && renderStep0()}
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
        </Spin>
      </Modal>

      {/* 临时结果统计页弹窗 */}
      <Modal
        title="临时结果统计页"
        open={showTempStatsModal}
        onCancel={() => setShowTempStatsModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowTempStatsModal(false)}>
            关闭
          </Button>
        ]}
        width={1200}
        centered
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}
      >
        {tempMetricResult ? (
          <AutoReviewStatsDisplay 
            data={tempMetricResult} 
            title="调试结果统计"
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            暂无数据
          </div>
        )}
      </Modal>

      {/* 指标统计脚本输出预览弹窗 - 全屏显示 */}
      <Modal
        title={
          <Space>
            <BarChartOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
            <span>机审结果统计页预览</span>
            <Tag color="blue">调试输出</Tag>
          </Space>
        }
        open={showMetricOutputPreview}
        onCancel={() => {
          setShowMetricOutputPreview(false);
          setMetricOutputPreviewData(null);
        }}
        footer={[
          <Button 
            key="close" 
            type="primary"
            onClick={() => {
              setShowMetricOutputPreview(false);
              setMetricOutputPreviewData(null);
            }}
          >
            关闭
          </Button>
        ]}
        width="95%"
        centered
        style={{ top: 10, paddingBottom: 10 }}
        bodyStyle={{ 
          height: 'calc(100vh - 120px)', 
          overflow: 'auto', 
          padding: '24px'
        }}
        destroyOnClose
      >
        {metricOutputPreviewData ? (
          <AutoReviewStatsDisplay 
            data={metricOutputPreviewData} 
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            暂无数据
          </div>
        )}
      </Modal>



    </>
  );
}

export default AiScriptGenerateModal;
